import { useState, useEffect } from 'react';
import { CreditCard, CheckCircle2, AlertCircle, PlusCircle, History } from 'lucide-react';
import { billingApi, type Plan, type Subscription, type PaymentMethodRef, type BillingHistory } from '../api/billing';
import MultiStepBilling from './portal/MultiStepBillingPage';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDate } from '../utils/formatTime';

export default function SubscriptionsPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [stripeActive, setStripeActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [methods, setMethods] = useState<PaymentMethodRef[]>([]);
  const [methodsNote, setMethodsNote] = useState('');
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [history, setHistory] = useState<BillingHistory | null>(null);
  const [histLoading, setHistLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [plansData, subsData] = await Promise.all([
        billingApi.getPlans(),
        billingApi.getSubscriptions(),
      ]);
      setPlans(plansData.plans);
      setStripeActive(plansData.stripe_active);
      setSubscriptions(subsData.subscriptions);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const loadMethods = async () => {
    setMethodsLoading(true);
    try { const d = await billingApi.getPaymentMethods(true); setMethods(d.payment_methods); setMethodsNote(d.note); }
    catch { setMethods([]); } finally { setMethodsLoading(false); }
  };
  const loadHistory = async () => {
    setHistLoading(true);
    try { setHistory(await billingApi.getHistory()); } catch { setHistory(null); } finally { setHistLoading(false); }
  };

  useEffect(() => { load(); loadMethods(); loadHistory(); }, []);

  const addPaymentMethod = async () => {
    setError('');
    try {
      const res = await billingApi.startCardSetup(
        `${window.location.origin}/app/settings/subscriptions?card=success`,
        `${window.location.origin}/app/settings/subscriptions?card=cancel`,
      );
      window.location.href = res.checkout_url;
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not start card setup.');
    }
  };

  const handleSubscribe = async (plan: Plan) => {
    if (plan.price_cents === 0) return;
    setProcessing(plan.plan_key);
    setError('');
    try {
      const result = await billingApi.checkout(
        plan.plan_key,
        `${window.location.origin}/settings/subscriptions?status=success`,
        `${window.location.origin}/settings/subscriptions?status=cancel`,
      );
      window.location.href = result.checkout_url;
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to start checkout');
      setProcessing(null);
    }
  };

  const handlePortal = async () => {
    try {
      const result = await billingApi.openPortal(`${window.location.origin}/settings/subscriptions`);
      window.location.href = result.portal_url;
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const isSubscribed = (planKey: string) => {
    return subscriptions.some(s => s.plan_key === planKey && s.status === 'active');
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-950 dark:text-slate-100">Billing</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Manage your plan, payment methods, and billing history.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-400">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Plan + add-ons — multi-layer selection (owner directive 2026-09-26) */}
      <div>
        <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100 mb-4">Upgrade &amp; Add-ons</h2>
        {!stripeActive && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2.5 text-sm text-amber-700 dark:text-amber-400">
            <AlertCircle size={16} />
            Billing is not yet active. The administrator needs to configure Stripe first.
          </div>
        )}
        <MultiStepBilling plans={plans} />
      </div>

      {/* Active subscriptions */}
      {subscriptions.length > 0 && (
        <Card className="p-5">
          <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100 mb-4">Your Subscriptions</h2>
          <div className="space-y-3">
            {subscriptions.map(sub => (
              <div key={sub.id} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 py-3 last:border-0">
                <div>
                  <div className="font-medium text-slate-950 dark:text-slate-100">{sub.plan_name}</div>
                  <div className="text-xs text-slate-500">
                    Status: <span className={`font-medium ${sub.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>{sub.status}</span>
                    {sub.current_period_end && ` · Renews: ${formatDate(sub.current_period_end)}`}
                  </div>
                </div>
                {sub.status === 'active' && (
                  <Button variant="secondary" onClick={handlePortal} className="text-xs">
                    Manage
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Payment methods */}
      <Card className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <CreditCard size={18} className="text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100">Payment Methods</h2>
        </div>
        {methodsLoading ? (
          <div className="flex h-16 items-center justify-center"><LoadingSpinner size="md" /></div>
        ) : methods.length === 0 ? (
          <p className="text-sm text-slate-500">No saved cards yet.</p>
        ) : (
          <div className="space-y-1">
            {methods.map(m => (
              <div key={m.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="text-sm capitalize text-slate-700 dark:text-slate-300">{m.brand || m.provider} ···· {m.last4 || '----'}</span>
                <span className="text-xs text-slate-500">{m.exp_month && m.exp_year ? `${String(m.exp_month).padStart(2, '0')}/${m.exp_year}` : ''}{m.is_default ? ' · default' : ''}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={addPaymentMethod} disabled={!stripeActive}><PlusCircle size={14} />Add card</Button>
          <Button size="sm" variant="secondary" onClick={() => loadMethods()} disabled={methodsLoading}><History size={14} />Refresh</Button>
        </div>
        {methodsNote && <p className="mt-3 text-xs text-slate-400">{methodsNote}</p>}
      </Card>

      {/* Billing history */}
      <Card className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <History size={18} className="text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100">Billing History</h2>
        </div>
        {histLoading ? (
          <div className="flex h-16 items-center justify-center"><LoadingSpinner size="md" /></div>
        ) : (
          <div className="space-y-5">
            <div>
              <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Payments</h3>
              {(history?.payments.length ?? 0) === 0 ? <p className="text-sm text-slate-500">No payments recorded yet.</p> : (
                <div className="space-y-1">
                  {history!.payments.map(p => (
                    <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                      <span className="text-xs text-slate-500">{p.event_type}{p.created_at ? ` · ${formatDate(p.created_at)}` : ''}</span>
                      <span className="text-sm text-slate-950 dark:text-slate-100">{p.amount_cents != null ? `$${(p.amount_cents / 100).toFixed(2)}` : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {subscriptions.length > 0 && (
        <div className="text-center">
          <button onClick={handlePortal} className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
            Manage billing in Stripe Customer Portal →
          </button>
        </div>
      )}
    </div>
  );
}